/**
 * operator_notification_jobs + operator_notification_deliveries
 * (Phase 17 / Task 17.1, DEC-324 — migration 0130).
 *
 * Hand-written to match `0130_operator_load_notification_outbox.sql`; that migration is
 * authoritative for every CHECK and for the SECURITY DEFINER unsubscribe functions.
 *
 *   operator_notification_jobs        one row per completed job, written by
 *                                     applyCompletionReview inside the completion
 *                                     transaction. UNIQUE (job_id), so a send-back and
 *                                     re-completion never queues twice.
 *   operator_notification_deliveries  one row per (job, recipient), expanded by the
 *                                     sender sweeper and drained with a claim/lease.
 */
import {
  index,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { tenants } from './users.js';
import { jobs } from './jobs.js';
import { siteContacts } from './site-contacts.js';
import { requestors } from './requestors.js';
import { appUserRole } from './enums.js';

export const OPERATOR_NOTIFICATION_JOB_OUTCOMES = [
  'QUEUED',
  'NOT_URGENT',
  'NOTIFICATIONS_OFF',
  'NO_RECIPIENTS',
  'NO_CUSTOMER',
  'NOT_COMPLETED',
] as const;
export type OperatorNotificationJobOutcome = (typeof OPERATOR_NOTIFICATION_JOB_OUTCOMES)[number];

export const operatorNotificationJobs = pgTable(
  'operator_notification_jobs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    processedAt: timestamp('processed_at', { withTimezone: true }),
    outcome: text('outcome'),
  },
  (t) => [
    unique('operator_notification_jobs_job_uq').on(t.jobId),
    index('operator_notification_jobs_pending_idx')
      .on(t.tenantId, t.createdAt)
      .where(sql`processed_at IS NULL`),
    pgPolicy('operator_notification_jobs_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
      withCheck: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
    }),
  ],
);

export const operatorNotificationDeliveries = pgTable(
  'operator_notification_deliveries',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    /** 'SITE_CONTACT' | 'REQUESTOR' (REQUESTOR is task 17.8, DEC-324 D-13). */
    recipientKind: text('recipient_kind').notNull(),
    siteContactId: uuid('site_contact_id').references(() => siteContacts.id, {
      onDelete: 'cascade',
    }),
    requestorId: uuid('requestor_id').references(() => requestors.id, { onDelete: 'cascade' }),
    /** Lowercased address at queue time — the per-load dedupe key across kinds. */
    email: text('email').notNull(),
    /** 'QUEUED' | 'SENT' | 'FAILED' | 'SUPPRESSED'. */
    status: text('status').notNull().default('QUEUED'),
    suppressReason: text('suppress_reason'),
    attempts: integer('attempts').notNull().default(0),
    nextAttemptAt: timestamp('next_attempt_at', { withTimezone: true }).notNull().defaultNow(),
    lastError: text('last_error'),
    sesMessageId: text('ses_message_id'),
    sentAt: timestamp('sent_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('operator_notification_deliveries_contact_uq').on(t.jobId, t.siteContactId),
    unique('operator_notification_deliveries_requestor_uq').on(t.jobId, t.requestorId),
    unique('operator_notification_deliveries_email_uq').on(t.jobId, t.email),
    index('operator_notification_deliveries_due_idx')
      .on(t.tenantId, t.nextAttemptAt)
      .where(sql`status = 'QUEUED'`),
    pgPolicy('operator_notification_deliveries_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
      withCheck: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
    }),
  ],
);
